import { useEffect, useCallback } from 'react'
import { useMutation, useLazyQuery } from '@apollo/client/react'

import {
  GET_CART,
  CART_CREATE,
  CART_LINES_ADD,
  CART_LINES_UPDATE,
  CART_LINES_REMOVE,
  CART_DISCOUNT_CODES_UPDATE,
} from '../components/Queries'
import { cartStore, useStore } from '../store/uiStore'

// Chiave localStorage per riprendere il carrello tra una visita e l'altra
const CART_ID_KEY = 'momoz_cart_id'

// Flag a livello di modulo: il ripristino da localStorage va fatto una volta sola
let cartRestored = false

// ============================================================
// Hook carrello Shopify (Cart API Storefront).
// Il carrello vive su Shopify: qui teniamo solo una copia in
// cartStore + il cartId in localStorage. Il checkout avviene
// redirezionando a cart.checkoutUrl (pagina di checkout Shopify).
// ============================================================
export default function useCart() {
  const cart = useStore(cartStore)

  const [loadCart] = useLazyQuery(GET_CART, { fetchPolicy: 'network-only' })
  const [cartCreate] = useMutation(CART_CREATE)
  const [linesAdd, addState] = useMutation(CART_LINES_ADD)
  const [linesUpdate, updateState] = useMutation(CART_LINES_UPDATE)
  const [linesRemove, removeState] = useMutation(CART_LINES_REMOVE)
  const [codesUpdate] = useMutation(CART_DISCOUNT_CODES_UPDATE)

  // --- Ripristino carrello esistente all'avvio ---
  useEffect(() => {
    if (cartRestored) return
    cartRestored = true
    const savedId = localStorage.getItem(CART_ID_KEY)
    if (!savedId) return

    loadCart({ variables: { cartId: savedId } })
      .then(({ data }) => {
        if (data?.cart) {
          cartStore.set(data.cart)
        } else {
          // Carrello scaduto o ordine completato: si ricomincia
          localStorage.removeItem(CART_ID_KEY)
        }
      })
      .catch(() => {})
  }, [loadCart])

  // --- Crea il carrello se non esiste ancora ---
  const ensureCartId = useCallback(async () => {
    const existing = cartStore.get()
    if (existing?.id) return existing.id

    const { data } = await cartCreate()
    const newCart = data?.cartCreate?.cart
    if (!newCart) {
      throw new Error(data?.cartCreate?.userErrors?.[0]?.message || 'Creazione carrello fallita')
    }
    cartStore.set(newCart)
    localStorage.setItem(CART_ID_KEY, newCart.id)
    return newCart.id
  }, [cartCreate])

  // --- Aggiungi una variante al carrello ---
  const addLine = useCallback(async (merchandiseId, quantity = 1) => {
    const cartId = await ensureCartId()
    const { data } = await linesAdd({
      variables: { cartId, lines: [{ merchandiseId, quantity }] },
    })
    const updated = data?.cartLinesAdd?.cart
    if (!updated) {
      throw new Error(data?.cartLinesAdd?.userErrors?.[0]?.message || 'Aggiunta al carrello fallita')
    }
    cartStore.set(updated)
    return updated
  }, [ensureCartId, linesAdd])

  // --- Cambia quantità di una riga ---
  const updateLine = useCallback(async (lineId, quantity) => {
    const cartId = cartStore.get()?.id
    if (!cartId) return
    const { data } = await linesUpdate({
      variables: { cartId, lines: [{ id: lineId, quantity }] },
    })
    if (data?.cartLinesUpdate?.cart) cartStore.set(data.cartLinesUpdate.cart)
  }, [linesUpdate])

  // --- Rimuovi una riga ---
  const removeLine = useCallback(async (lineId) => {
    const cartId = cartStore.get()?.id
    if (!cartId) return
    const { data } = await linesRemove({
      variables: { cartId, lineIds: [lineId] },
    })
    if (data?.cartLinesRemove?.cart) cartStore.set(data.cartLinesRemove.cart)
  }, [linesRemove])

  // --- Aggiungi un codice sconto (il premio della macchinetta) ---
  // Il carrello si crea anche vuoto: il codice ci resta attaccato e scatta
  // quando arriva la merce. Si AGGIUNGE ai codici che c'erano, perche' la
  // mutation sostituisce la lista intera.
  const applyDiscountCode = useCallback(async (code) => {
    const cartId = await ensureCartId()
    const presenti = (cartStore.get()?.discountCodes || []).map((d) => d.code)
    if (presenti.includes(code)) return cartStore.get()
    const { data } = await codesUpdate({
      variables: { cartId, discountCodes: [...presenti, code] },
    })
    const updated = data?.cartDiscountCodesUpdate?.cart
    if (!updated) {
      throw new Error(data?.cartDiscountCodesUpdate?.userErrors?.[0]?.message || 'Codice non applicato')
    }
    cartStore.set(updated)
    return updated
  }, [ensureCartId, codesUpdate])

  return {
    cart,
    applyDiscountCode,
    addLine,
    updateLine,
    removeLine,
    adding: addState.loading,
    updating: updateState.loading || removeState.loading,
  }
}

// Formattatore prezzi condiviso ("12,50 €")
export function formatPrice(moneyV2) {
  if (!moneyV2) return ''
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: moneyV2.currencyCode || 'EUR',
  }).format(Number(moneyV2.amount))
}
